// src/components/sacred/charts.jsx
//
// S9 — Token-bound Recharts primitives.
// ---------------------------------------------------------------------
// Wraps Recharts CartesianGrid / XAxis / YAxis / Tooltip with Sacred
// Canvas defaults (chartTokens for grid, axis, tooltipBg/Fg; sacred
// fonts for tick + label families) so chart-using screens stop
// duplicating these prop blobs. Token-only — no inline hex except the
// values already encoded in chartTokens (which DESIGN.md documents
// the OKLCH equivalents for).
//
// displayName is set to the underlying Recharts component name so
// the parent BarChart / LineChart still matches them by displayName
// when iterating children.

import React from 'react';
import {
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from '@mui/material';
import { chartTokens } from '../../theme/sacredCanvas';

// Hook that yields the active mode's chart palette.
export const useChartTokens = () => {
  const theme = useTheme();
  return chartTokens[theme.palette.mode] || chartTokens.light;
};

// Sacred-Canvas grid: dashed, low-contrast, mode-aware stroke.
export const ChartGrid = (props) => {
  const c = useChartTokens();
  return (
    <CartesianGrid strokeDasharray="2 4" stroke={c.grid} {...props} />
  );
};
ChartGrid.displayName = 'CartesianGrid';

// Generic axis wrapper that accepts orientation. Prefer the
// specific exports below at call sites for clarity.
const sharedAxisDefaults = (c, fontVar) => ({
  tick: {
    fontFamily: `var(--sacred-font-${fontVar})`,
    fontSize: fontVar === 'body' ? 12 : 11,
    fill: c.axis,
  },
  axisLine: false,
  tickLine: false,
});

export const ChartXAxis = (props) => {
  const c = useChartTokens();
  return <XAxis {...sharedAxisDefaults(c, 'body')} {...props} />;
};
ChartXAxis.displayName = 'XAxis';

export const ChartYAxis = (props) => {
  const c = useChartTokens();
  return <YAxis {...sharedAxisDefaults(c, 'label')} {...props} />;
};
ChartYAxis.displayName = 'YAxis';

// Generic ChartAxis — orientation prop chooses x vs y. Useful when
// a call site builds axes dynamically.
export const ChartAxis = ({ orientation = 'x', ...props }) => {
  const c = useChartTokens();
  const fontVar = orientation === 'y' ? 'label' : 'body';
  const Cmp = orientation === 'y' ? YAxis : XAxis;
  return <Cmp {...sharedAxisDefaults(c, fontVar)} {...props} />;
};
ChartAxis.displayName = 'XAxis';

// Sacred-Canvas tooltip: cream surface, emerald label, soft shadow,
// mode-aware. Cursor uses the grid token so the highlight feels
// like the same paper.
export const ChartTooltip = (props) => {
  const c = useChartTokens();
  return (
    <Tooltip
      contentStyle={{
        backgroundColor: c.tooltipBg,
        border: 'none',
        borderRadius: 12,
        fontFamily: 'var(--sacred-font-body)',
        boxShadow: '0 10px 30px -10px rgba(31,95,74,0.25)',
      }}
      labelStyle={{
        color: c.tooltipFg,
        fontFamily: 'var(--sacred-font-display)',
        fontWeight: 700,
        marginBottom: 6,
      }}
      itemStyle={{
        color: c.tooltipFg,
        fontFamily: 'var(--sacred-font-body)',
      }}
      cursor={{ fill: c.grid }}
      {...props}
    />
  );
};
ChartTooltip.displayName = 'Tooltip';
